import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Camera, Phone, Shield } from 'lucide-react';

interface WelcomeStepProps {
  onNext: () => void;
}

const FEATURES = [
  { icon: MessageCircle, labelKey: 'wizard.welcome.featureChat',    color: 'text-primary' },
  { icon: Camera,        labelKey: 'wizard.welcome.featurePhotos',  color: 'text-accent' },
  { icon: Phone,         labelKey: 'wizard.welcome.featureCalls',   color: 'text-primary' },
  { icon: Shield,        labelKey: 'wizard.welcome.featurePrivacy', color: 'text-primary' },
] as const;

// The warm Irish greeting and the feature highlights are shown together on a
// single screen. There is deliberately NO auto-advance timer: senior-UX
// research is clear that time pressure on the first screen is where elders
// abandon, and the step previously dismissed its greeting after 1.8s.
export function WelcomeStep({ onNext }: WelcomeStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo / Branding */}
        <div className="mb-6">
          <motion.img
            src="/logo.webp"
            alt="Chlann"
            className="w-24 h-24 mb-4 drop-shadow-2xl"
            draggable={false}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 180, damping: 16, delay: 0.05 }}
          />
          <p
            className="font-display text-xl md:text-2xl text-primary tracking-wide mb-3"
            style={{ fontStyle: 'italic', fontVariationSettings: '"opsz" 32' }}
          >
            Is maith liom do theaghlach a fheiceáil.
          </p>
          <h2
            className="font-display text-3xl font-bold text-ink-1 mb-2 tracking-tight"
            style={{ fontVariationSettings: '"opsz" 32' }}
          >
            {t('wizard.welcome.title')}
          </h2>
          <p className="text-ink-3 max-w-sm mx-auto text-sm leading-relaxed">
            {t('wizard.welcome.subtitle')}
          </p>
        </div>

        {/* Feature highlights */}
        <div className="grid grid-cols-2 gap-3 mb-8 max-w-sm mx-auto">
          {FEATURES.map(({ icon: Icon, labelKey, color }, i) => (
            <motion.div
              key={labelKey}
              className="flex items-center gap-2 p-3 rounded-xl bg-surface-2/60 border border-line-1/50 text-sm text-ink-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.08, duration: 0.3 }}
            >
              <Icon className={`w-4 h-4 ${color} shrink-0`} />
              <span>{t(labelKey)}</span>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.button
          onClick={onNext}
          className="btn-primary w-full max-w-sm"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t('wizard.welcome.getStarted')}
        </motion.button>
      </motion.div>
    </div>
  );
}
